/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import type { TFunction } from 'i18next';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';
import U2000ActionBtn from '../../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000DetailTable from '../../../../u2000Core/components/tables/detail/U2000DetailTable';
import { U2000IdColumnDef, type U2000TableColumn } from '../../../../u2000Core/components/tables/basic/U2000BasicTableList';
import U2000ResponsiveToolbar from '../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar';
import { useU2000AuthContext } from '../../../../u2000Core/context/auth/U2000UseAuthContext';
import { U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000GridStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { NS, U3206ROLES } from '../../../U3206';
import { useU3206Context } from '../../../U3206Context';
import type { U3206AiUserGroupFormRow, U3206GroupMissionLink } from '../../../U3206Service';
import U3206MissionsAddDlg from './U3206MissionsAddDlg';

type U3206MissionGridRow = U3206GroupMissionLink & { id: string };

interface U3206MissionsTabProps {
  formStateHandlers: U2000FormStateHandlers<U3206AiUserGroupFormRow>;
  t: TFunction;
}

function toGridRow(link: U3206GroupMissionLink): U3206MissionGridRow {
  return {
    ...link,
    id: link.id ?? link.missionId,
  };
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    { ...U2000IdColumnDef },
    {
      name: 'code',
      label: t('lblMissionCode', { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: 'w-[30%]',
      type: 'string',
      align: 'left',
    },
    {
      name: 'title',
      label: t('lblMissionTitle', { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: 'w-[70%]',
      type: 'string',
      align: 'left',
    },
  ];
}

function U3206MissionsTab({ formStateHandlers, t }: U3206MissionsTabProps) {
  const { initLU } = useU3206Context();
  const { hasRole } = useU2000AuthContext();
  const data = formStateHandlers.getData();
  const isEditMode = formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT;
  const missions = data?.missions ?? [];
  const [addOpen, setAddOpen] = useState(false);
  const gridStateHandlers = useU2000GridStateHandlers<U3206MissionGridRow>();

  const missionsDataKey = useMemo(() => {
    const ids = missions.map((m) => m.missionId).filter(Boolean).sort();
    return `${data?.id ?? ''}_${missions.length}_${ids.join(',')}`;
  }, [data?.id, missions]);

  const setMissions = (next: U3206GroupMissionLink[]) => {
    formStateHandlers.onChange?.(
      { target: { id: 'missions', value: next } } as unknown as ChangeEvent<HTMLInputElement>,
      next,
    );
  };

  useEffect(() => {
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(
      columns
        .filter((col) => col.isDefaultSortedColumn)
        .map((col) => ({ name: col.name, sortOrder: 'ASC' as const })),
    );
    const list = missions.map(toGridRow);
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu
  }, [missionsDataKey, t]);

  const selectedRows = gridStateHandlers.getSelectedRows();
  const canEdit =
    isEditMode && (hasRole(U3206ROLES.EDIT) || hasRole(U3206ROLES.ADD));
  const deleteDisabled = !canEdit || selectedRows.length === 0;

  const handleDelete = () => {
    if (deleteDisabled) return;
    const selectedIds = new Set(selectedRows.map((row) => row.missionId));
    setMissions(missions.filter((m) => !selectedIds.has(m.missionId)));
  };

  const handleConfirmAdd = (links: U3206GroupMissionLink[]) => {
    const existing = new Set(missions.map((m) => m.missionId));
    const toAdd = links.filter((link) => !existing.has(link.missionId));
    if (toAdd.length === 0) return;
    setMissions([...missions, ...toAdd]);
  };

  const actions = useMemo(
    () => [
      {
        key: 'add',
        label: t('btnAdd', { ns: NS }),
        handleClick: () => setAddOpen(true),
        hasRole: hasRole(U3206ROLES.EDIT) || hasRole(U3206ROLES.ADD),
        disabled: !canEdit || initLU.missions.length === 0,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: 'delete',
        label: t('btnDelete', { ns: NS }),
        handleClick: handleDelete,
        hasRole: hasRole(U3206ROLES.EDIT) || hasRole(U3206ROLES.ADD),
        disabled: deleteDisabled,
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleDelete stable via missions snapshot
    [t, canEdit, deleteDisabled, hasRole, initLU.missions.length, selectedRows.length, missionsDataKey],
  );

  if (initLU.missions.length === 0 && missions.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('lblNoMissions', { ns: NS })}</p>;
  }

  return (
    <>
      <U3206MissionsAddDlg
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onConfirm={handleConfirmAdd}
        catalog={initLU.missions}
        linkedMissionIds={missions.map((m) => m.missionId)}
        t={t}
      />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={canEdit}
        label={t('lblMissionList', { count: gridStateHandlers.getTotalRows(), ns: NS })}
      />
    </>
  );
}

export default U3206MissionsTab;
